import { createServer, Response } from "miragejs";
import { host } from "utils/http-service";

import { createGroups } from "../mocks/groups";
import { createQueue } from "../mocks/queue";
import { createBlockAppointmentResponse } from "../mocks/blockAppointmentResponse";
import { createAppointment } from "../mocks/appointment";
import { createAppointmentActionResponse } from "../mocks/appointmentActionResponse";
import { createIntervalList } from "../mocks/intervalList";
import { createContext } from "../mocks/context";
import { createUserAppointments } from "../mocks/userAppointments";
import { createRegistrationConfirmationResponse } from "../mocks/registrationConfirmationResponse";
import { createUserAppointment } from "../mocks/userAppointment";
import { createUser } from "../mocks/user";
import { createSsoLoginForm } from "../mocks/ssoLoginForm";
import { LoggedUserStatus } from "../../modules/user/application/types/LoggedUserStatus";
import { LoggingMode } from "../../modules/context/types/LoggingMode";

export const mocksInitializer = () =>
  createServer({
    routes() {
      const friendlyUrl = "default";

      this.timing = 500;
      this.urlPrefix = host;

      this.get(`/e-reservation/${friendlyUrl}/groups`, () => createGroups(14));
      this.get(
        `/e-reservation/${friendlyUrl}/queue/:queueId`,
        (schema, request) =>
          new Response(200, undefined, createQueue(request.params.queueId))
      );
      this.post(`/e-reservation/${friendlyUrl}/appointment/block`, () =>
        createBlockAppointmentResponse()
      );
      this.post(`/e-reservation/${friendlyUrl}/appointment/book`, () => {
        return { status: 200 };
      });
      this.post(
        `/e-reservation/${friendlyUrl}/appointment/confirm/:tokenId`,
        () => createAppointmentActionResponse()
      );
      this.get(
        `/e-reservation/${friendlyUrl}/appointment/:appointmentId`,
        () => new Response(200, undefined, createAppointment())
      );
      this.get(`/e-reservation/${friendlyUrl}/appointment/edit/:tokenId`, () =>
        createAppointmentActionResponse()
      );
      this.get(
        `/e-reservation/${friendlyUrl}/appointment/cancel/:tokenId`,
        () => createAppointmentActionResponse()
      );
      this.post(
        `/e-reservation/${friendlyUrl}/appointment/cancel/:tokenId`,
        () => {
          return { status: 200 };
        }
      );
      this.post(`/e-reservation/${friendlyUrl}/appointment/edit/:tokenId`, () =>
        createAppointmentActionResponse()
      );
      this.get(
        `/e-reservation/${friendlyUrl}/queue/:queueId/intervals`,
        (schema, request) => createIntervalList(request.queryParams.month)
      );
      this.get("/contexts/recaptcha", () => {
        return {
          googleRecaptchaId: "6LdxLNQZAAAAAKWdQH5ColDKvHutAtt0iBjlRayH",
        };
      });
      this.get(`/contexts/${friendlyUrl}/e-reservation`, () =>
        createContext({
          loggingInMode: [
            LoggingMode.Sso,
            LoggingMode.Guest,
            LoggingMode.Local,
          ],
        })
      );
      this.get(`/e-reservation/${friendlyUrl}/user/:tokenSecurity`, () =>
        createUser()
      );
      this.post(
        `/e-reservation/account-activation/:tokenId`,
        () =>
          new Response(200, undefined, createRegistrationConfirmationResponse())
      );
      this.get(
        `/e-reservation/user/:userId/appointments`,
        (schema, request) => {
          const offset = Number(request.queryParams.offset) ?? 0;
          return createUserAppointments(125, 6, offset);
        }
      );
      this.get(
        `/e-reservation/user/:userId/appointment/:appointmentId/details`,
        (schema, request) => {
          return createUserAppointment({
            appointmentId: request.params.appointmentId,
          });
        }
      );
      this.get(
        `/e-reservation/get-user-email/:tokenId`,
        () => new Response(200, undefined, { email: "dragon.dc@gmail.com" })
      );
      this.post(`/generate-new-password/:tokenId`, () => {
        return { status: 200 };
      });
      this.post(`/e-reservation/log-in`, () => createUser());
      this.post(`/user/:userId/log-out`, () => {
        return { status: 200 };
      });
      this.post(
        `/e-reservation/user/:userId/appointment/:appointmentId/edit/:intervalId`,
        () => new Response(200, undefined, createAppointmentActionResponse())
      );
      this.get(`/e-reservation/user/:userId/info`, () => createUser());
      this.post(
        `/e-reservation/user/:userId/appointment/:appointmentId/cancel`,
        () => {
          return { status: 200 };
        }
      );
      this.post(`/user/:userId/change-old-password`, () => {
        return { status: 200 };
      });
      this.post(`/e-reservation/user/:userId/delete`, () => {
        return { status: 200 };
      });
      this.post(`/e-reservation/${friendlyUrl}/remind-password`, () => {
        return { status: 200 };
      });
      this.post(
        `/e-reservation/user/:userId/appointment/:appointmentId/confirm`,
        () => {
          return { status: 200 };
        }
      );
      this.post(`/e-reservation/user/:userId/edit`, () => {
        return { status: 200 };
      });
      this.get(
        `/e-reservation/${friendlyUrl}/log-in-sso/html-form`,
        () => new Response(200, undefined, createSsoLoginForm())
      );
      this.get(
        `/e-reservation/${friendlyUrl}/user/:userId/connect-sso/html-form`,
        () => new Response(200, undefined, createSsoLoginForm())
      );
      this.post(
        `/e-reservation/${friendlyUrl}/fill-log-in-sso-data/:ssoToken`,
        () => new Response(200, undefined, createUser())
      );
      this.post(`/user/:userId/disconnect-sso`, () => new Response(200));
      this.post(
        `/user/:userId/accept-terms`,
        () => new Response(200, undefined)
      );
      this.get(
        `/e-reservation/user/:userId/temp-appointment`,
        () => new Response(200, undefined, { appointmentId: null })
      );
      this.post(
        `/e-reservation/account-sso-activation/:tokenSecurity`,
        () => new Response(200, undefined, createUser())
      );
      this.post(
        `/e-reservation/log-in-sso/:tokenSecurity`,
        () =>
          new Response(200, undefined, {
            user: createUser({
              forceToCheckAgreementTerms: 0,
              status: LoggedUserStatus.Active,
            }),
          })
      );
      this.post(
        `/e-reservation/${friendlyUrl}/register`,
        () =>
          new Response(400, undefined, {
            msg: [
              {
                error: "Istnieje już konto o podanym adresie e-mail w systemie",
                name: "email",
              },
              {
                error: "Nie przekazano wartości pola.",
                name: "phone",
              },
            ],
          })
      );
      this.get(
        `/e-reservation/sso-user/:tokenId`,
        () => new Response(200, undefined, createUser({ ssoConnection: 1 }))
      );
    },
  });
